import React from 'react';
import { motion } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { Mail, ArrowDown, ChevronRight } from 'lucide-react';

const HeroV3 = ({ data }) => {
    return (
        <header className="min-h-screen flex items-center justify-center relative overflow-hidden">
            {/* Animated gradient orbs */}
            <div className="absolute inset-0 overflow-hidden">
                <motion.div
                    animate={{
                        x: [0, 30, -20, 0],
                        y: [0, -40, 20, 0],
                        scale: [1, 1.1, 0.95, 1],
                    }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[128px]"
                />
                <motion.div
                    animate={{
                        x: [0, -40, 30, 0],
                        y: [0, 20, -30, 0],
                        scale: [1, 0.9, 1.1, 1],
                    }}
                    transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-secondary/8 rounded-full blur-[128px]"
                />
                <motion.div
                    animate={{
                        x: [0, 20, -30, 0],
                        y: [0, 30, -20, 0],
                    }}
                    transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-primary-glow/5 rounded-full blur-[100px]"
                />
            </div>

            {/* Subtle dot grid */}
            <div className="absolute inset-0 bg-[radial-gradient(rgba(59,130,246,0.08)_1px,transparent_1px)] [background-size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_30%,transparent_100%)]" />

            {/* Top line accent */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

            <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
                {/* Availability badge */}
                <motion.div
                    initial={{ opacity: 0, y: -20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.6, type: "spring" }}
                    className="mb-10"
                >
                    <span className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-primary/20 bg-primary/5 backdrop-blur-sm text-primary-light text-xs font-medium tracking-wider uppercase">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
                        </span>
                        Available for Remote Opportunities
                    </span>
                </motion.div>

                {/* Name with gradient */}
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-display font-bold text-white mb-6 tracking-[-0.04em] leading-[0.9]"
                >
                    <span className="bg-gradient-to-b from-white via-white to-text-tertiary bg-clip-text text-transparent">
                        {data.name}
                    </span>
                </motion.h1>

                {/* Static role title */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="mb-4"
                >
                    <span className="text-lg sm:text-xl md:text-2xl font-medium text-text-secondary tracking-wide">
                        Platform Support Engineer
                    </span>
                </motion.div>

                {/* Typewriter subtitles */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="text-base sm:text-lg md:text-xl font-light mb-10 h-8 md:h-10 flex items-center justify-center"
                >
                    <TypeAnimation
                        sequence={[
                            'Cloud Support (AWS / Azure)',
                            3000,
                            'Incident Response & RCA',
                            2500,
                            'Python & Node.js Automation',
                            2500,
                            'DevRev Platform Administration',
                            2500,
                            'SaaS Operations & SLA Management',
                            2500,
                        ]}
                        wrapper="span"
                        speed={40}
                        className="text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-secondary-light"
                        repeat={Infinity}
                    />
                </motion.div>

                {/* Description */}
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.7 }}
                    className="text-text-tertiary max-w-xl mx-auto text-base leading-relaxed mb-12"
                >
                    {data.description}
                </motion.p>

                {/* CTA buttons */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.9 }}
                    className="flex flex-col sm:flex-row justify-center gap-4"
                >
                    <a
                        href={`mailto:${data.email}`}
                        className="group relative inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-primary text-white rounded-xl font-semibold overflow-hidden shadow-glow hover:shadow-glow-lg transition-all duration-500"
                    >
                        <Mail size={18} />
                        <span className="relative z-10">Get In Touch</span>
                        <div className="absolute inset-0 bg-gradient-primary opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </a>
                    <a
                        href="#experience"
                        className="group inline-flex items-center justify-center gap-2 px-8 py-4 text-text-secondary hover:text-white transition-colors duration-300"
                    >
                        <span>View Experience</span>
                        <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </a>
                </motion.div>

                {/* Scroll indicator */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2, duration: 1 }}
                    className="absolute bottom-10 left-1/2 -translate-x-1/2"
                >
                    <motion.div
                        animate={{ y: [0, 8, 0] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                        className="flex flex-col items-center gap-2"
                    >
                        <span className="text-[10px] text-text-tertiary uppercase tracking-[0.2em]">Scroll</span>
                        <ArrowDown size={14} className="text-text-tertiary" />
                    </motion.div>
                </motion.div>
            </div>
        </header>
    );
};

export default HeroV3;
